import { useEffect, useState } from "react";
import { InlineMath } from "react-katex";
import { constructInputWithSpecialExpressionList, checkAnswerState } from "../../../utilization/CatchtivityUtilization";
import ShowMaterialMediaByContentType from "./ShowMaterialMediaByContentType";
import { IMCMAActivityMaterialProps } from "../../../properties/ActivityProperties";
import i18n from "../../../language/i18n";
import { resolveMaterialMediaType } from "../../../utilization/StorageUtilization";

const MCMAActivityMaterialContent = ({
  uniqueValue,
  answerMap,
  materialMap,
  contentMap,
  checkCanAnswerQuestion,
  onChange,
  isPreview,
  showCorrectAnswer,
}: IMCMAActivityMaterialProps) => {
  const [displayAnswerMap, setDisplayAnswerMap] = useState<any>(answerMap);

  useEffect(() => {
    if (showCorrectAnswer) {
      // Create correct answer map where each key maps to the correct answer list
      const correctAnswerMap: any = {};
      Object.keys(materialMap).forEach((materialKey) => {
        // The correct answers are stored as a JSON string in the first key
        correctAnswerMap[materialKey] = JSON.parse(materialKey);
      });
      setDisplayAnswerMap(correctAnswerMap);
    } else {
      setDisplayAnswerMap(answerMap);
    }
  }, [showCorrectAnswer, answerMap, materialMap]);

  const retrieveCorrectAnswerList = () => {
    return JSON.parse(Object.keys(materialMap)[0]);
  };

  const correctAnswerList = retrieveCorrectAnswerList();
  // When locked (submitted / time exceeded / duel-contest already answered) the
  // learner still sees the full options view with their picks highlighted, but
  // it is non-interactive.
  const canAnswer = checkCanAnswerQuestion();
  // "Your answer" affordances (the pick ring, the legend) apply only when we are
  // showing a learner's OWN answer — not a plain correct-answer reveal
  // (showCorrectAnswer swaps displayAnswerMap for the answer key, so every box
  // would read as "picked & correct"). Mirrors TrueFalseActivityMaterialContent.
  const showLearnerMarks = Boolean(isPreview) && !showCorrectAnswer;
  // `|| {}` so a malformed activity degrades instead of white-screening — this
  // runs above the per-option guards and cannot borrow them.
  const hasLearnerChoice = Object.keys(materialMap).some(
    (materialKey) => ((displayAnswerMap || {})[materialKey] || []).length > 0
  );
  // ✓/✗ answer "was I right?" whenever there is a learner answer to grade — the
  // convention Matching/Grouping/Ordering/Dropdown already follow. With no learner
  // answer at all this degrades to a bare key reveal, where ✓-on-key is fine.
  const gradeAgainstLearner = showLearnerMarks && hasLearnerChoice;
  const showLegend = gradeAgainstLearner;

  return (<div className="mc-root">{Object.keys(materialMap).map((materialKey, index) => {
    return (
      <div key={index} className="mc-section">
        <div className="mc-panel">
          <div className="mc-scroll">
            <div className={`mc-options${canAnswer ? "" : " mc-locked"}`}>
              {materialMap[materialKey].map(
                (materialSubKey: string, index: number) => {
                  const currentDisplayAnswerList = displayAnswerMap[materialKey] || [];
                  const foundAnswer = currentDisplayAnswerList.find(
                    (learnerAnswer: string) => learnerAnswer === materialSubKey
                  );
                  const learnerAnswerState = !isPreview
                    ? "HIDDEN"
                    : materialSubKey !== foundAnswer
                    ? "EMPTY"
                    : checkAnswerState(correctAnswerList, foundAnswer);
                  const foundIndex = correctAnswerList.findIndex(
                    (correctAnswer: string) => correctAnswer === materialSubKey
                  );
                  const graded = learnerAnswerState !== "HIDDEN";
                  const isCorrectOption = foundIndex !== -1;
                  const isPickedOption =
                    currentDisplayAnswerList.includes(materialSubKey);
                  // A TICKED box means "I ticked it" — nothing else. It used to be
                  // switched on for every correct option too, so a learner who found
                  // one of three correct answers saw three ticks and read a
                  // partly-right answer as fully right ("neyi seçtiğimi unutursam
                  // 'yanlış yapmamışım' diye düşünebilirim" — a real learner report).
                  // A correct option they MISSED is now a green-outlined, UNticked
                  // box (ig-cbx-key), and their own ticks additionally carry a ring.
                  const boxOn = gradeAgainstLearner
                    ? isPickedOption
                    : isPickedOption || (graded && isCorrectOption);
                  const boxStateClass =
                    (graded && isCorrectOption
                      ? gradeAgainstLearner && !isPickedOption
                        ? " ig-cbx-key"
                        : " ig-cbx-correct"
                      : graded && isPickedOption && !isCorrectOption
                        ? " ig-cbx-wrong"
                        : isPickedOption
                          ? " ig-cbx-on"
                          : "") +
                    (showLearnerMarks && isPickedOption ? " ig-cbx-picked" : "");
                  return (
                    <div
                      key={index}
                      className={`mc-option mc-option-gap ${
                        (learnerAnswerState === "EMPTY" && isCorrectOption) ||
                        learnerAnswerState === "CORRECT"
                          ? "mc-correct"
                          : learnerAnswerState === "INCORRECT"
                          ? "mc-incorrect"
                          : ""
                      }${
                        learnerAnswerState === "HIDDEN" && isPickedOption
                          ? " mc-option-sel"
                          : ""
                      }`}
                      onClick={() => {
                        if (canAnswer)
                          onChange(answerMap, materialKey, materialSubKey);
                      }}
                    >
                      <span className={`ig-cbx-box${boxStateClass}`}>
                        {boxOn ? (
                          <span className="ig-cbx-check">
                            <svg viewBox="0 0 14 14" width={11} height={11}>
                              <path
                                d="M2 7.2l3 3 7-7.2"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2.4}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        ) : null}
                      </span>
                      <div className="mc-option-body">
                        {resolveMaterialMediaType(materialSubKey, contentMap.type) === "TEXT" ? (
                          <p className="mc-text">
                            {constructInputWithSpecialExpressionList(
                              materialSubKey
                            ).map((inputPart, index) => (
                              <span
                                key={index}
                                className={`${
                                  inputPart.isBold ? "mc-bold" : ""
                                } ${inputPart.isUnderline ? "mc-underline" : ""}`}
                              >
                                {inputPart.isEquation ? (
                                  <span className="mc-xl">
                                    <InlineMath math={inputPart.value} />
                                  </span>
                                ) : (
                                  inputPart.value
                                )}
                              </span>
                            ))}
                          </p>
                        ) : (
                          <ShowMaterialMediaByContentType
                            key={`${uniqueValue}-${index}`}
                            contentType={resolveMaterialMediaType(materialSubKey, contentMap.type)}
                            src={materialSubKey}
                            canFullScreen={true}
                          />
                        )}
                      </div>
                      {graded &&
                      isCorrectOption &&
                      (gradeAgainstLearner ? isPickedOption : true) ? (
                        <span className="mc-fb mc-fb--ok">✓</span>
                      ) : null}
                      {graded && isPickedOption && !isCorrectOption ? (
                        <span className="mc-fb mc-fb--no">✗</span>
                      ) : null}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </div>
      </div>
    );
  })}
  {showLegend ? (
    <div className="mc-legend">
      <span className="mc-legend-item">
        <span className="ig-cbx-box ig-cbx-key" />
        {i18n.t("activity_correct_answer")}
      </span>
      <span className="mc-legend-item">
        <span className="ig-cbx-box ig-cbx-picked" />
        {i18n.t("activity_your_answer")}
      </span>
    </div>
  ) : null}
  </div>);
};

export default MCMAActivityMaterialContent;
